import dayjs from 'dayjs'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'
import { Input, InputGroup, InputLeftAddon } from '@wartek-id/input'
import { Table } from '@wartek-id/table'

import SingleLayout from '../../components/Layouts/SingleLayout'
import FeedbackBanner from '../../components/Modal Survei/Survei'
import { trackSatuEvent } from '../../lib/satu-event-tracker'

type statusVariant =
  | 'critical'
  | 'warning'
  | 'success'
  | 'informational'
  | 'neutral'
  | 'expressive-success'
  | 'expressive-warning'
  | 'expressive-critical'
  | 'expressive-informational'
  | 'expressive-neutral'
  | undefined

type Ticket = {
  id: string
  subject: string
  status: string
  created_at: string
  updated_at: string
  requester_id: number
}

type UserData = {
  email: string
  name: string
  user_id: string
}

type Comments = {
  id: number
  author_id: number
  body: string
  html_body: string
  plain_body: string
  public: boolean
  attachments: []
  audit_id: number
  created_at: string
}

enum TicketStatus {
  NEW = 'Dalam Antrian',
  HOLD = 'Sedang Diproses',
  SOLVED = 'Sudah Selesai',
}

enum TicketColor {
  NEW = 'warning',
  HOLD = 'informational',
  SOLVED = 'success',
}

function getStatusDesc(status: string) {
  switch (status) {
    case 'new':
    default:
      return null
    case 'open':
      return (
        <p className="text-left italic text-xs text-mutedGray itali mt-2">
          <sup>*</sup>Sedang ditangani oleh petugas
        </p>
      )
    case 'hold':
      return (
        <p className="text-left italic text-xs text-mutedGray itali mt-2">
          <sup>*</sup>Sedang di eskalasi ke bagian terkait
        </p>
      )
    case 'pending':
      return (
        <p className="text-left italic text-xs text-mutedGray itali mt-2">
          <sup>*</sup>Menunggu balasan lanjutan dari pelapor
        </p>
      )
    case 'solved':
    case 'closed':
      return null
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'new':
    default:
      return TicketStatus.NEW
    case 'open':
    case 'hold':
    case 'pending':
      return TicketStatus.HOLD
    case 'solved':
    case 'closed':
      return TicketStatus.SOLVED
  }
}

function getStatusVariant(status: string): statusVariant {
  switch (status) {
    case 'new':
    default:
      return TicketColor.NEW
    case 'open':
    case 'hold':
    case 'pending':
      return TicketColor.HOLD
    case 'solved':
    case 'closed':
      return TicketColor.SOLVED
  }
}

export default function TicketPage() {
  const [ticketId, setTicketId] = useState('')

  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [user, setUser] = useState<UserData | null>(null)
  // statuses : 'not_found', 'in_progress', 'error', 'found'
  const [status, setStatus] = useState('')
  const [comments, setComments] = useState<Comments[] | null>(null)
  const [lastComment, setLastComment] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Track page view on mount (equivalent to Vue's onMounted)
  useEffect(() => {
    trackSatuEvent({
      activity_type: 'PAGE_VIEW',
      page_name: 'LACAK STATUS TICKET',
    })
  }, [])

  const validateTicketId = () => {
    if (!ticketId.trim()) {
      setError('Silakan isi nomor tiket untuk melacak status tiket laporan.')
      setStatus('error')
      return false
    }
    setError('')
    setStatus('')
    return true
  }

  const resetState = () => {
    setError('')
    setStatus('')
    setTicket(null)
    setUser(null)
    setComments(null)
    setLastComment('')
  }

  useEffect(() => {
    const findTheLatestCommentByAgent = () => {
      const requesterId = ticket?.requester_id
      if (!comments || !requesterId) {
        return null
      }
      // Make sure comments is treated as an array
      const commentsArray = Array.isArray(comments) ? comments : [comments]
      // Filter for public comments not authored by the requester
      const filteredByPublicAndAgent = commentsArray.filter((comment) => {
        return comment.public === true && comment.author_id !== requesterId
      })
      // Sort filtered comments by latest creation date
      const latestComment = filteredByPublicAndAgent.sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      })[0]

      if (latestComment) {
        setLastComment(latestComment.created_at)
      } else {
        setLastComment('')
        return null
      }
    }
    findTheLatestCommentByAgent()
  }, [comments, ticket])

  const handleSearch = async () => {
    setLoading(true)
    resetState()

    if (!validateTicketId()) {
      setLoading(false)
      return
    }

    // Track search submission
    trackSatuEvent({
      activity_type: 'SUBMIT_PENCARIAN_TIKET',
      page_name: 'LACAK STATUS TICKET',
      additional_data: [
        {
          key: 'text_input',
          value: ticketId,
        },
      ],
    })

    try {
      const res = await fetch(`/api/tickets/fetch-ticket?ticketId=${ticketId}`)
      if (!res.ok) {
        if (res.status === 404) {
          setTicket(null)
          setStatus('not_found')
          setLoading(false)

          // Track ticket not found
          trackSatuEvent({
            activity_type: 'SUBMIT_PENCARIAN_TIKET',
            page_name: 'LACAK STATUS TICKET',
            additional_data: [
              {
                key: 'text_input',
                value: ticketId,
              },
              {
                key: 'response',
                value: 'TICKET_NOT_FOUND',
              },
            ],
          })

          return
        }
        setStatus('in_progress')
        throw new Error('Fetch failed')
      }
      const data = await res.json()
      setTicket(data.ticket)
      setUser(data.user)
      setComments(data.comments)
      setStatus('found')

      // Track ticket found
      trackSatuEvent({
        activity_type: 'SUBMIT_PENCARIAN_TIKET',
        page_name: 'LACAK STATUS TICKET',
        additional_data: [
          {
            key: 'text_input',
            value: ticketId,
          },
          {
            key: 'response',
            value: 'TICKET_FOUND',
          },
        ],
      })
    } catch (err) {
      setTicket(null)
      setStatus('not_found')

      // Track ticket not found (error case)
      trackSatuEvent({
        activity_type: 'SUBMIT_PENCARIAN_TIKET',
        page_name: 'LACAK STATUS TICKET',
        additional_data: [
          {
            key: 'text_input',
            value: ticketId,
          },
          {
            key: 'response',
            value: 'TICKET_NOT_FOUND',
          },
        ],
      })
    }
    setLoading(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setTicketId(e.target.value)
      handleSearch()
    }
  }

  const tableData = [
    {
      title: 'Nomor Tiket',
      value: ticket ? ticket.id : '-',
    },
    {
      title: 'E-mail Pelapor',
      value: user?.email ? user.email : '-',
    },
    {
      title: 'Judul Laporan',
      value: ticket ? ticket.subject : '-',
    },
    {
      title: 'Tanggal Laporan',
      value: ticket
        ? dayjs(ticket.created_at).format('DD MMMM YYYY HH:mm:ss')
        : '-',
    },
    {
      title: 'Tanggal Balasan Terakhir',
      value: lastComment
        ? dayjs(lastComment).format('DD MMMM YYYY HH:mm:ss')
        : '-',
    },
    {
      title: 'Status Tiket',
      value: ticket ? getStatusLabel(ticket.status) : '-',
    },
  ]

  return (
    <SingleLayout>
      <div className="flex flex-col justify-center items-center min-h-[60vh] pt-[160px] md:pt-[140px]">
        <div className="w-full md:w-[70vw] mx-auto bg-white py-2 md:py-8 px-[6vw] my-4 md:my-8 text-center">
          <h1 className="font-semibold text-[24px] leading-[28px] text-center mb-8">
            Lacak Status Tiket Laporan
          </h1>
          <h3 className="font-semibold text-charcoal text-[18px] leading-6 mb-3 text-left">
            Masukkan nomor tiket Anda
          </h3>
          <div className="flex flex-col md:flex-row items-center gap-2 mb-1">
            <InputGroup>
              <InputLeftAddon>
                <Icon color="inherit">search</Icon>
              </InputLeftAddon>
              <Input
                placeholder="Contoh: 012345679"
                onChange={(e) => setTicketId(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
            <Button
              as={Button}
              color="blue"
              size="lg"
              className="mb-2 md:mb-0 w-full md:w-[160px]"
              disabled={loading}
              onClick={handleSearch}
            >
              Lacak
            </Button>
          </div>
          <div className="mb-8 text-left">
            {status === '' && !loading && (
              <p className="text-sm text-mutedGray italic">
                Nomor tiket diperoleh melalui email ketika Anda mengirimkan
                laporan ke kanal pengaduan ULT
              </p>
            )}
          </div>
        </div>

        {loading && (
          <div className="my-4 mb-[60px]">
            <Image
              src="/images/loading.gif"
              width={200}
              height={100}
              alt="loading"
              className="m-auto"
            />
          </div>
        )}

        {(status === 'not_found' ||
          status === 'in_progress' ||
          status === 'error') && (
          <div className="bg-lightGray w-full px-6 md:px-8 lg:px-0">
            <div className="ticket-container mx-auto my-[56px] md:my-[80px] px-0 lg:px-8">
              <h3 className="font-semibold text-charcoal text-[22px] leading-7 mt-8 mb-6">
                Hasil Pencarian Tiket
              </h3>
              <div className="border border-mutedGray rounded-md bg-white p-4 md:p-6 mb-[60px]">
                <div className="flex flex-col lg:flex-row items-center gap-6 my-4 justify-center">
                  <div className="flex flex-col items-end">
                    <Image
                      src="/images/img-search.png"
                      width={110}
                      height={110}
                      alt="search"
                    />
                  </div>
                  {status === 'not_found' && (
                    <div className="flex flex-col items-start text-left">
                      <h2 className="font-semibold text-charcoal text-[22px] leading-[28px] md:text-[26px] md:leading-[36px]">
                        Nomor Tiket Tidak Ditemukan
                      </h2>
                      <p className="text-charcoal text-[15px] leading-[22px] md:text-[20px] md:leading-[28px]">
                        Pastikan nomor tiket laporan Anda sudah sesuai atau coba
                        lagi nanti.
                      </p>
                    </div>
                  )}
                  {status === 'in_progress' && (
                    <div className="flex flex-col items-start text-left">
                      <h2 className="font-semibold text-charcoal text-[22px] leading-[28px] md:text-[26px] md:leading-[36px]">
                        Tiket Anda Dalam Antrian, Coba Lagi
                      </h2>
                      <p className="text-charcoal text-[15px] leading-[22px] md:text-[20px] md:leading-[28px]">
                        Saat ini, Anda telah mencapai batas maksimum tiket yang
                        dapat diproses. Silakan coba lagi dalam beberapa saat.
                      </p>
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="flex flex-col items-start text-left">
                      <h2 className="font-semibold text-charcoal text-[22px] leading-[28px] md:text-[26px] md:leading-[36px]">
                        Nomor Tiket Wajib Diisi
                      </h2>
                      <p className="text-charcoal text-[15px] leading-[22px] md:text-[20px] md:leading-[28px]">
                        {error}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {ticket && (
          <div className="bg-lightGray w-full px-6 md:px-8">
            <div className="ticket-container mx-auto hidden md:block">
              <div className="ticket-content mx-auto mt-[48px] mb-5">
                <h3 className="font-semibold text-charcoal text-[22px] leading-7 mt-8 mb-6">
                  Status Tiket Laporan
                </h3>
                <div className="border border-mutedGray hidden md:block">
                  <Table size="md" bordered fullWidth>
                    <thead>
                      <tr>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure !border-l-azure"
                        >
                          NOMOR TIKET
                        </th>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure"
                        >
                          E-MAIL PELAPOR
                        </th>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure"
                        >
                          JUDUL LAPORAN
                        </th>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure"
                        >
                          TANGGAL LAPORAN
                        </th>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure"
                        >
                          TANGGAL BALASAN TERAKHIR
                        </th>
                        <th
                          align="center"
                          className="bg-azure text-white text-[12px] !border-t-azure !border-r-azure w-[160px]"
                        >
                          STATUS TIKET
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td align="left">
                          <p>{ticket.id}</p>
                        </td>
                        <td align="left">
                          <p>{user?.email ? user?.email : '-'}</p>
                        </td>
                        <td align="left">
                          <p>{ticket.subject}</p>
                        </td>
                        <td align="left">
                          <p>
                            {ticket.created_at
                              ? dayjs(ticket.created_at).format(
                                  'DD MMMM YYYY HH:mm:ss',
                                )
                              : '-'}
                          </p>
                        </td>
                        <td align="left">
                          <p>
                            {lastComment
                              ? dayjs(lastComment).format(
                                  'DD MMMM YYYY HH:mm:ss',
                                )
                              : '-'}
                          </p>
                        </td>
                        <td align="center">
                          <Badge
                            variant={getStatusVariant(ticket.status)}
                            size="md"
                          >
                            {getStatusLabel(ticket.status)}
                          </Badge>
                          {getStatusDesc(ticket.status)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div className="px-2 mt-4">
                  <p className="text-xs text-mutedGray italic">
                    Jawaban terakhir atas tiket laporan dapat Anda cek di email
                    yang tercantum di atas
                  </p>
                </div>
              </div>
            </div>

            <div className="block md:hidden my-[48px]">
              <h3 className="font-semibold text-charcoal text-[22px] leading-7 mt-8 mb-7">
                Status Tiket Laporan
              </h3>
              <div className="border border-charcoal w-full">
                <div className="overflow-hidden">
                  {tableData.map((row, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border-b border-mutedGray last:border-b-0"
                    >
                      <div className="flex items-center bg-azure text-left px-4 py-3 min-w-[45vw] min-h-[10vh]">
                        <p className="text-sm font-semibold text-white">
                          {row.title}
                        </p>
                      </div>
                      <div className="flex flex-grow text-sm text-left px-4 py-3">
                        {row.title === 'Status Tiket' ? (
                          <Badge
                            variant={getStatusVariant(ticket.status)}
                            size="md"
                          >
                            {getStatusLabel(ticket.status)}
                          </Badge>
                        ) : (
                          <p className="text-sm break-all">{row.value}</p>
                        )}
                        {getStatusDesc(ticket.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-2 mt-2">
                <p className="text-xs text-mutedGray italic">
                  Jawaban terakhir atas tiket laporan dapat Anda cek di email
                  yang tercantum di atas
                </p>
              </div>
            </div>

            <FeedbackBanner surveyUrl="https://surveidigital.spbe.go.id/embed/survey/eyJzdXJ2ZXlfaWQiOjIsInNlcnZpY2VfaWQiOjM0OSwiaG9zdCI6Imh0dHBzOi8vdWx0LmtlbWVuZGlrZGFzbWVuLmdvLmlkLyxodHRwczovL3VsdC5hdW0uc3RhZ2luZy5iZWxhamFyLmlkLyxodHRwOi8vbG9jYWxob3N0OjMwMDAvLGh0dHBzOi8vcHVzYXRpbmZvcm1hc2kudWx0LmtlbWVuZGlrZGFzbWVuLmdvLmlkLyIsImtleSI6ImVvbzVXMzVaIn0=/embed/view/?jenis_layanan=Website ULT - Lacak Status Tiket Laporan" />

            <div className="ticket-container mx-auto">
              <div className="ticket-content mx-auto mt-[48px] mb-3">
                <h3 className="font-semibold text-charcoal text-[22px] leading-7 mt-[60px]">
                  Alur Kerja Tiket Laporan
                </h3>
              </div>
            </div>

            <div className="flex items-center justify-center mb-[80px]">
              <div
                className="
                  inline-flex
                  flex-col md:flex-row
                  items-center
                  justify-center
                  gap-2
                  p-4"
              >
                <Image
                  src="/images/ticket/img-ticket-status-01.png"
                  alt="Status Tiket 1"
                  width={280}
                  height={160}
                  className="mb-2 md:mb-0 md:mr-[-90px] img-status-ticket"
                />
                <Image
                  src="/images/ticket/img-ticket-line.png"
                  alt="Separator Horizontal"
                  width={290}
                  height={4}
                  className="mb-[80px] hidden md:block img-status-ticket-line"
                />
                <Image
                  src="/images/ticket/img-ticket-line-vertical.png"
                  alt="Separator Vertical"
                  width={4}
                  height={40}
                  className="mb-2 block md:hidden"
                />
                <Image
                  src="/images/ticket/img-ticket-status-02.png"
                  alt="Status Tiket 2"
                  width={300}
                  height={160}
                  className="mb-2 md:mb-0 md:mx-[-100px] img-status-ticket"
                />
                <Image
                  src="/images/ticket/img-ticket-line.png"
                  alt="Separator Horizontal"
                  width={290}
                  height={4}
                  className="mb-[80px] hidden md:block img-status-ticket-line"
                />
                <Image
                  src="/images/ticket/img-ticket-line-vertical.png"
                  alt="Separator Vertical"
                  width={4}
                  height={40}
                  className="mb-2 block md:hidden"
                />
                <Image
                  src="/images/ticket/img-ticket-status-03.png"
                  alt="Status Tiket 3"
                  width={280}
                  height={160}
                  className="mb-2 md:mb-0 md:ml-[-90px] img-status-ticket"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </SingleLayout>
  )
}
